import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Work as WorkIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Storage as StorageIcon,
  Link as LinkIcon
} from '@mui/icons-material';

const AgentOverviewDetails = ({ agent }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Busy': return 'warning';
      case 'Available': return 'info';
      case 'Inactive': return 'error';
      case 'Training': return 'secondary';
      case 'Offboarded': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <CheckCircleIcon />;
      case 'Busy': return <AccessTimeIcon />;
      case 'Available': return <WorkIcon />;
      case 'Inactive': return <WorkIcon />;
      case 'Training': return <WorkIcon />;
      case 'Offboarded': return <WorkIcon />;
      default: return <WorkIcon />;
    }
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';
    const now = new Date();
    const last = new Date(lastActive);
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Agent Overview
      </Typography>
      
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar src={agent.icon} alt={agent.name} sx={{ width: 60, height: 60, mr: 2 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {agent.name}
                  </Typography>
                  <Chip
                    icon={getStatusIcon(agent.status)}
                    label={agent.status}
                    color={getStatusColor(agent.status)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Category" 
                    secondary={agent.category}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email Address" 
                    secondary={agent.emailConfig?.emailAddress || 'Not configured'}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Hire Date" 
                    secondary={new Date(agent.hireDate).toLocaleDateString()}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Configuration Status" 
                    secondary={agent.isConfigured ? 'Configured' : 'Needs Onboarding'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                Performance Metrics
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary.contrastText">
                      {agent.performance?.tasksCompleted || 0}
                    </Typography>
                    <Typography variant="body2" color="primary.contrastText">
                      Tasks Completed
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="success.contrastText">
                      {agent.performance?.successRate || 100}%
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                      Success Rate
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="info.contrastText">
                      {agent.performance?.hoursWorked || 0}
                    </Typography>
                    <Typography variant="body2" color="info.contrastText">
                      Hours Worked
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight="bold" color="warning.contrastText">
                      {formatLastActive(agent.performance?.lastActive)}
                    </Typography>
                    <Typography variant="body2" color="warning.contrastText">
                      Last Active
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Custom Context */}
        {agent.customContext && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ mr: 1 }} />
                  Custom Context
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {agent.customContext}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* External Context */}
        {agent.externalContext && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinkIcon sx={{ mr: 1 }} />
                  External Context
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {agent.externalContext}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Settings & Configuration
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Working Hours
                  </Typography>
                  <Typography variant="body2">
                    {agent.settings?.workingHours?.start || '09:00'} - {agent.settings?.workingHours?.end || '17:00'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Timezone
                  </Typography>
                  <Typography variant="body2">
                    {agent.settings?.timezone || 'UTC'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Email Communication
                  </Typography>
                  <Typography variant="body2">
                    {agent.emailConfig?.isEmailEnabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Language
                  </Typography>
                  <Typography variant="body2">
                    {agent.settings?.language || 'English'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AgentOverviewDetails; 